import {
  ApplicationConfig,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import {
  provideRouter,
  RouteReuseStrategy,
  TitleStrategy,
  withComponentInputBinding,
  withExperimentalAutoCleanupInjectors,
  withInMemoryScrolling,
} from '@angular/router';

import { routes } from './app.routes';
import { AppTitleStrategy } from '@core/services';
import { NTT_VALIDATION_MESSAGES } from '@ui/form-field';
import { NTT_TOAST_CONFIG, NttToastGlobalConfig } from '@ui/alert';
import { provideCoreServices } from '@core/core.provider';
import { AnalyticsService } from '@core/services';
import { environment } from '../environments/environment';
import {
  APP_CONFIG,
  AppConfig,
  GA_MEASUREMENT_ID,
  RECAPTCHA_SITE_KEY,
} from '@core/config';
import { SvgRegistry } from '@ui/svg';
import { inject, isDevMode } from '@angular/core';
import { SelectiveReuseStrategy } from '@core/strategies';
import { provideServiceWorker } from '@angular/service-worker';
import { THEME_TOGGLE_ENABLED } from '@shared/services';

function registerSvgAssets(): void {
  const registry = inject(SvgRegistry);

  registry.add('bacart-logo', '/logo/bacart-logo.svg');
  registry.add('bacart-logo-light', '/logo/bacart-logo-light.svg');
  registry.add('bacart-logo-dark', '/logo/bacart-logo-dark..svg');
  registry.add('bacart-logo-h', '/logo/bacart-logo-horizontal.svg');

  registry.add(
    'bg-pattern-dark',
    '/backgrounds/background-pattern-dark-mode.svg',
  );
  registry.add(
    'bg-pattern-light',
    '/backgrounds/background-pattern-light-mode.svg',
  );
  registry.add(
    'bg-pattern-mobile-dark',
    '/backgrounds/background-pattern-mobile-dark-mode.svg',
  );
  registry.add(
    'bg-pattern-mobile-light',
    '/backgrounds/background-pattern-mobile-light-mode.svg',
  );

  registry.add(
    'illustration-box-sm',
    '/assets/illustrations/illustration-box-sm.svg',
  );
  registry.add(
    'illustration-box-md',
    '/assets/illustrations/illustration-box-md.svg',
  );
  registry.add(
    'illustration-box-lg',
    '/assets/illustrations/illustration-box-lg.svg',
  );
  registry.add(
    'illustration-cloud-sm',
    '/assets/illustrations/illustration-cloud-sm.svg',
  );
  registry.add(
    'illustration-cloud-md',
    '/assets/illustrations/illustration-cloud-md.svg',
  );
  registry.add(
    'illustration-cloud-lg',
    '/assets/illustrations/illustration-cloud-lg.svg',
  );
  registry.add(
    'illustration-credit-card-sm',
    '/assets/illustrations/illustration-credit-card-sm.svg',
  );
  registry.add(
    'illustration-credit-card-md',
    '/assets/illustrations/illustration-credit-card-md.svg',
  );
  registry.add(
    'illustration-credit-card-lg',
    '/assets/illustrations/illustration-credit-card-lg.svg',
  );
  registry.add(
    'illustration-documents-sm',
    '/assets/illustrations/illustration-documents-sm.svg',
  );
  registry.add(
    'illustration-documents-md',
    '/assets/illustrations/illustration-documents-md.svg',
  );
  registry.add(
    'illustration-documents-lg',
    '/assets/illustrations/illustration-documents-lg.svg',
  );
  registry.add(
    'empty-cart-state',
    '/assets/illustrations/empty-cart-state.svg',
  );
}

function initializeAnalytics(): void {
  inject(AnalyticsService).init();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withExperimentalAutoCleanupInjectors(),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
    ),
    provideCoreServices(),
    provideAppInitializer(registerSvgAssets),
    provideAppInitializer(initializeAnalytics),
    DatePipe,
    {
      provide: NTT_VALIDATION_MESSAGES,
      useValue: {
        required: () => 'وارد کردن این فیلد الزامی است',
        minLength: () => 'حداقل تعداد کاراکتر رعایت نشده',
        maxLength: () => 'حداکثر تعداد کاراکتر رعایت نشده',
        min: () => 'مقدار حداقل رعایت نشده',
        max: () => 'مقدار حداکثر رعایت نشده',
        email: () => 'ایمیل وارد شده اشتباه است',
        pattern: () => 'نوع ورودی معتبر نیست',
        passwordMismatch: () => 'تکرار رمز عبور مطابقت ندارند',
        invalidPhone: () => 'شماره موبایل وارد شده صحیح نمی‌باشد.',
        invalidEmail: () => 'ایمیل وارد شده صحیح نمی‌باشد.',
      },
    },
    {
      provide: APP_CONFIG,
      useValue: {
        apiUrl: environment.api.url,
        production: environment.production,
        authClientId: environment.authClientId,
      } as AppConfig,
    },
    {
      provide: GA_MEASUREMENT_ID,
      useValue: environment.gaMeasurementId,
    },
    {
      provide: RECAPTCHA_SITE_KEY,
      useValue: environment.recaptchaSiteKey,
    },
    {
      provide: NTT_TOAST_CONFIG,
      useValue: {
        duration: 3000,
        position: 'top-center',
      } as NttToastGlobalConfig,
    },
    {
      provide: THEME_TOGGLE_ENABLED,
      useValue: environment.devTools,
    },
    {
      provide: TitleStrategy,
      useClass: AppTitleStrategy,
    },
    {
      provide: RouteReuseStrategy,
      useClass: SelectiveReuseStrategy,
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
